import type { MessageKey, Translator } from "~/i18n";

import type { ToolCallDescription, WorkEntryStepSource } from "./workEntryStep";
import { describeToolCall, resolveWorkEntryToolName } from "./workEntryStep";

const EMAIL_ACTION_KEYS: Readonly<Record<string, MessageKey>> = {
  read_email: "chat.tool.readEmail",
  search_email: "chat.tool.searchEmail",
  send_email: "chat.tool.sendEmail",
};

const BROWSER_TAIL_KEYS: Readonly<Record<string, MessageKey>> = {
  navigate: "chat.tool.browser.navigate",
  click: "chat.tool.browser.click",
  type: "chat.tool.browser.type",
  screenshot: "chat.tool.browser.screenshot",
  scroll: "chat.tool.browser.scroll",
  wait: "chat.tool.browser.wait",
  extract: "chat.tool.browser.extract",
  fill: "chat.tool.browser.fill",
  back: "chat.tool.browser.back",
  forward: "chat.tool.browser.forward",
  close: "chat.tool.browser.close",
  open: "chat.tool.browser.open",
};

const WHATSAPP_TAIL_KEYS: Readonly<Record<string, MessageKey>> = {
  send: "chat.tool.whatsapp.send",
  read: "chat.tool.whatsapp.read",
};

const VAULT_TOOL_ACTION_KEYS: Readonly<Record<string, MessageKey>> = {
  list_vault_items: "chat.tool.vault.list",
  search_vault: "chat.tool.vault.search",
  read_vault_item: "chat.tool.vault.read",
  get_vault_item_link: "chat.tool.vault.shareLink",
  make_attachment_public: "chat.tool.vault.makePublic",
};

function normalizeToolToken(toolName: string): string {
  return toolName
    .replace(/^\[subagent\]\s*/i, "")
    .trim()
    .toLowerCase();
}

function humanizeTail(raw: string): string {
  const value = raw
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
  if (!value) {
    return "";
  }
  return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
}

function localizedComposioSearchAction(toolName: string, t: Translator): string {
  const cleaned = toolName.replace(/^\[subagent\]\s*/i, "").trim();
  const suffix = cleaned.replace(/^composio[_-]?search(?:[_-]|$)/i, "").trim();
  if (!suffix) {
    return t("chat.tool.searchWeb");
  }
  const normalized = suffix.replace(/[_-]+/g, "").toLowerCase();
  if (normalized === "news") {
    return t("chat.tool.searchNews");
  }
  const human = humanizeTail(suffix);
  if (!human) {
    return t("chat.tool.searchWeb");
  }
  if (!/\bsearch\b/i.test(human)) {
    return t("chat.tool.searchTopic", { topic: human.toLowerCase() });
  }
  return human;
}

/** Localize built-in / Morrow-native tool labels for the active UI language. */
export function localizeToolCallDescription(
  desc: ToolCallDescription,
  toolName: string,
  t: Translator,
): ToolCallDescription {
  const cleaned = normalizeToolToken(toolName);
  let action = desc.action;
  let appName = desc.appName;

  const emailKey = EMAIL_ACTION_KEYS[cleaned];
  if (emailKey) {
    action = t(emailKey);
  } else if (cleaned.startsWith("browser_")) {
    const tail = cleaned.slice("browser_".length);
    const tailKey = BROWSER_TAIL_KEYS[tail];
    action = tailKey ? t(tailKey) : t("chat.tool.browser.generic", { action: humanizeTail(tail) });
    appName = t("chat.tool.app.browser");
  } else if (cleaned.startsWith("whatsapp_")) {
    const tail = cleaned.slice("whatsapp_".length);
    const tailKey = WHATSAPP_TAIL_KEYS[tail];
    action = tailKey ? t(tailKey) : t("chat.tool.message");
    appName = t("chat.tool.app.whatsapp");
  } else if (cleaned === "recall_memory") {
    action = t("chat.tool.memory.recall");
    appName = t("chat.tool.app.morrow");
  } else if (cleaned === "remember") {
    action = t("chat.tool.memory.save");
    appName = t("chat.tool.app.morrow");
  } else if (cleaned === "list_agents") {
    action = t("chat.tool.listAgents");
    appName = t("chat.tool.app.morrow");
  } else if (cleaned === "search_knowledge") {
    action = t("chat.tool.knowledge.search");
    appName = t("chat.tool.app.morrow");
  } else if (cleaned === "search_items") {
    action = t("chat.tool.searchItems");
    appName = t("chat.tool.app.morrow");
  } else if (cleaned === "show_item_image") {
    action = t("chat.tool.showImage");
  } else if (cleaned === "show_item_images") {
    action = t("chat.tool.showImages");
  } else if (cleaned === "generate_image") {
    action = t("chat.tool.generateImage");
  } else if (cleaned === "generate_pdf") {
    action = t("chat.tool.generatePdf");
  } else if (cleaned === "request_app_connection") {
    action = t("chat.tool.connectApp");
  } else if (cleaned === "activate_tools") {
    action = t("chat.tool.activateTools");
  } else if (VAULT_TOOL_ACTION_KEYS[cleaned]) {
    action = t(VAULT_TOOL_ACTION_KEYS[cleaned]);
    appName = t("chat.tool.app.vault");
  } else if (desc.slug === "spawn_subagent" || cleaned === "spawn_subagent") {
    action = t("chat.tool.spawnSubagent");
  } else if (desc.slug === "composio_search") {
    action = localizedComposioSearchAction(toolName, t);
    appName = t("chat.tool.app.morrow");
  } else if (desc.slug === "email") {
    appName = t("chat.tool.app.email");
  } else if (desc.slug === "browser") {
    appName = t("chat.tool.app.browser");
  } else if (desc.slug === "whatsapp") {
    appName = t("chat.tool.app.whatsapp");
  } else if (desc.slug === "vault") {
    appName = t("chat.tool.app.vault");
  } else if (desc.appName === "Morrow") {
    appName = t("chat.tool.app.morrow");
  }

  return { ...desc, action, appName };
}

export function localizedToolCallTitle(toolName: string, t: Translator): string {
  const desc = localizeToolCallDescription(describeToolCall(toolName), toolName, t);
  return desc.appName ? `${desc.appName} · ${desc.action}` : desc.action;
}

/** Work-log row heading — tool slugs and generic fallbacks. */
export function localizeWorkEntryHeading(
  source: WorkEntryStepSource,
  heading: string,
  t: Translator,
): string {
  const rawTool = resolveWorkEntryToolName(source);
  if (rawTool) {
    const desc = localizeToolCallDescription(describeToolCall(rawTool), rawTool, t);
    if (desc.appName && desc.action) {
      return `${desc.appName} · ${desc.action}`;
    }
    if (desc.action) {
      return desc.action;
    }
  }

  if (source.itemType === "web_search") {
    return source.toolLifecycleStatus === "inProgress"
      ? t("chat.work.searchingWeb")
      : t("chat.work.searchedWeb");
  }
  if (source.requestKind === "file-read" || source.itemType === "image_view") {
    return source.toolLifecycleStatus === "inProgress"
      ? t("chat.work.reading")
      : t("chat.work.read");
  }
  if (source.requestKind === "file-change" || source.itemType === "file_change") {
    return source.toolLifecycleStatus === "inProgress"
      ? t("chat.work.editingFiles")
      : t("chat.work.editedFiles");
  }
  if (source.requestKind === "command" || source.itemType === "command_execution") {
    return source.toolLifecycleStatus === "inProgress"
      ? t("chat.work.runningCommand")
      : t("chat.work.ranCommand");
  }
  if (source.tone === "thinking" && !source.label.trim()) {
    return t("chat.work.thinking");
  }
  if (source.itemType === "mcp_tool_call") {
    return source.toolLifecycleStatus === "inProgress"
      ? t("chat.work.usingApp")
      : t("chat.work.usedApp");
  }
  if (heading === "Working") {
    return t("chat.work.working");
  }
  if (heading === "Thinking") {
    return t("chat.work.thinking");
  }

  return heading;
}
